// Shared audit utilities for Reporting pages
// Contains target type metadata, schema-specific renderers, normalization, actor formatting, and CSV export helper
import axios from 'axios';
import React from 'react';

// Generic value formatter to ensure Mongo Decimal128 extended JSON objects
// (e.g. { $numberDecimal: "123.45" }) or BSON Longs ({ $numberLong: "123" })
// don't get passed directly as React children which causes:
//   "Objects are not valid as a React child (found: object with keys {$numberDecimal})"
// Also gracefully handles Dates, nested objects (stringifying), arrays and nullish values.
export const formatValue = (val, { suffix = '', pct = false } = {}) => {
    if (val === null || val === undefined) return '';
    // Decimal128 extended JSON
    if (typeof val === 'object') {
        if (val.$numberDecimal !== undefined) {
            const num = parseFloat(val.$numberDecimal);
            if (Number.isFinite(num)) return (pct ? num.toString() : num.toLocaleString()) + suffix;
            return String(val.$numberDecimal) + suffix;
        }
        if (val.$numberLong !== undefined) {
            const num = parseInt(val.$numberLong, 10);
            if (Number.isFinite(num)) return (pct ? num.toString() : num.toLocaleString()) + suffix;
            return String(val.$numberLong) + suffix;
        }
        if (val instanceof Date) return val.toLocaleString() + suffix;
        // Primitive-like wrappers
        if (val.valueOf && typeof val.valueOf() !== 'object') {
            const v = val.valueOf();
            if (typeof v === 'number') return (pct ? v.toString() : v.toLocaleString()) + suffix;
            return String(v) + suffix;
        }
        // Array -> JSON (compact) / join
        if (Array.isArray(val)) {
            return val.map(v => typeof v === 'object' ? formatValue(v) : String(v)).join(', ') + suffix;
        }
    }
    if (typeof val === 'number') {
        return (pct ? val.toString() : val.toLocaleString()) + suffix;
    }
    return String(val) + suffix;
};

export const targetTypeInfo = {
    Bond: { label: 'Bond', description: 'A bond instrument issued by the organization.' },
    Whitelist: { label: 'Whitelist Allocation', description: 'An investor allocation linking investor to bond.' },
    Wallet: { label: 'Wallet', description: 'A custodial wallet holding on-chain assets and banking details.' },
    Investor: { label: 'Investor', description: 'An entity or individual holding bonds.' },
    Payment: { label: 'Payment', description: 'A record of a payment or distribution.' },
    Compliance: { label: 'Compliance', description: 'A compliance record or document.' },
    User: { label: 'User', description: 'A user account in the system.' },
    TokenTransfer: { label: 'Token Transfer', description: 'A transfer of tokens between users or wallets.' },
    SecondaryTransfer: { label: 'Secondary Transfer', description: 'A secondary market transfer request.' },
};

export const schemaFormatters = {
    Bond: (t) => (
        <div className="mt-2 space-y-1 text-sm text-gray-700">
            {t.name && (<div><span className="font-semibold">Name:</span> {t.name}</div>)}
            {t.title && (<div><span className="font-semibold">Title:</span> {t.title}</div>)}
            {t.code && (<div><span className="font-semibold">Code:</span> {t.code}</div>)}
            {t.isin && (<div><span className="font-semibold">ISIN:</span> {t.isin}</div>)}
            {t.issuer && (<div><span className="font-semibold">Issuer:</span> {typeof t.issuer === 'object' ? (t.issuer.name || t.issuer.title || t.issuer._id) : t.issuer}</div>)}
            {t.couponRate !== undefined && (<div><span className="font-semibold">Coupon Rate:</span> {formatValue(t.couponRate, { pct: true, suffix: '%' })}</div>)}
            {t.couponFrequency && (<div><span className="font-semibold">Coupon Frequency:</span> {t.couponFrequency}</div>)}
            {t.issueDate && (<div><span className="font-semibold">Issue Date:</span> {new Date(t.issueDate).toLocaleDateString()}</div>)}
            {t.maturityDate && (<div><span className="font-semibold">Maturity Date:</span> {new Date(t.maturityDate).toLocaleDateString()}</div>)}
            {t.principal !== undefined && (<div><span className="font-semibold">Principal:</span> {formatValue(t.principal)}</div>)}
            {t.currency && (<div><span className="font-semibold">Currency:</span> {t.currency}</div>)}
            {t.status && (<div><span className="font-semibold">Status:</span> {t.status}</div>)}
        </div>
    ),
    Wallet: (t) => (
        <div className="mt-2 space-y-1 text-sm text-gray-700">
            {t.walletAddress && (<div><span className="font-semibold">Wallet Address:</span> <span className="break-all inline-block">{t.walletAddress}</span></div>)}
            {t.userId && (<div><span className="font-semibold">Owner:</span> {typeof t.userId === 'object' ? (t.userId.email || t.userId.name || t.userId._id) : t.userId}</div>)}
            {t.bankAccountDetails?.accountType && (<div><span className="font-semibold">Bank Account Type:</span> {t.bankAccountDetails.accountType}</div>)}
            {t.bankAccountDetails?.bankName && (<div><span className="font-semibold">Bank:</span> {t.bankAccountDetails.bankName}</div>)}
            {t.bankAccountDetails?.accountNumber && (<div><span className="font-semibold">Account No.:</span> {t.bankAccountDetails.accountNumber}</div>)}
            {t.status && (<div><span className="font-semibold">Status:</span> {t.status}</div>)}
            {t.network && (<div><span className="font-semibold">Network:</span> {t.network}</div>)}
            {t.walletType && (<div><span className="font-semibold">Type:</span> {t.walletType}</div>)}
            {t.riskProfile && (<div><span className="font-semibold">Risk Profile:</span> {t.riskProfile}</div>)}
            {t.createdAt && (<div><span className="font-semibold">Created:</span> {new Date(t.createdAt).toLocaleString()}</div>)}
            {t.lastActivity && (<div><span className="font-semibold">Last Activity:</span> {new Date(t.lastActivity).toLocaleString()}</div>)}
        </div>
    ),
    Investor: (t) => (
        <div className="mt-2 space-y-1 text-sm text-gray-700">
            {t.name && (<div><span className="font-semibold">Name:</span> {t.name}</div>)}
            {t.type && (<div><span className="font-semibold">Type:</span> {t.type}</div>)}
            {t.email && (<div><span className="font-semibold">Email:</span> {t.email}</div>)}
            {t.phone && (<div><span className="font-semibold">Phone:</span> {t.phone}</div>)}
            {t.country && (<div><span className="font-semibold">Country:</span> {t.country}</div>)}
            {t.registrationNumber && (<div><span className="font-semibold">Registration No.:</span> {t.registrationNumber}</div>)}
        </div>
    ),
    Payment: (t) => (
        <div className="mt-2 space-y-1 text-sm text-gray-700">
            {t.amount !== undefined && (<div><span className="font-semibold">Amount:</span> {formatValue(t.amount)}</div>)}
            {t.currency && (<div><span className="font-semibold">Currency:</span> {t.currency}</div>)}
            {t.status && (<div><span className="font-semibold">Status:</span> {t.status}</div>)}
            {t.date && (<div><span className="font-semibold">Date:</span> {new Date(t.date).toLocaleString()}</div>)}
            {t.method && (<div><span className="font-semibold">Method:</span> {t.method}</div>)}
        </div>
    ),
    Compliance: (t) => (
        <div className="mt-2 space-y-1 text-sm text-gray-700">
            {t.name && (<div><span className="font-semibold">Name:</span> {t.name}</div>)}
            {t.type && (<div><span className="font-semibold">Type:</span> {t.type}</div>)}
            {t.status && (<div><span className="font-semibold">Status:</span> {t.status}</div>)}
            {t.updatedAt && (<div><span className="font-semibold">Updated:</span> {new Date(t.updatedAt).toLocaleString()}</div>)}
        </div>
    ),
    User: (t) => (
        <div className="mt-2 space-y-1 text-sm text-gray-700">
            {t.email && (<div><span className="font-semibold">Email:</span> {t.email}</div>)}
            {t.name && (<div><span className="font-semibold">Name:</span> {t.name}</div>)}
            {t.role && (<div><span className="font-semibold">Role:</span> {t.role}</div>)}
            {t.parentAccountId && (<div><span className="font-semibold">Parent Account:</span> {typeof t.parentAccountId === 'object' ? (t.parentAccountId.email || t.parentAccountId._id) : t.parentAccountId}</div>)}
            {t.status && (<div><span className="font-semibold">Status:</span> {t.status}</div>)}
        </div>
    ),
    TokenTransfer: (t) => (
        <div className="mt-2 space-y-1 text-sm text-gray-700">
            {t.transferId && (<div><span className="font-semibold">Transfer ID:</span> {t.transferId}</div>)}
            {t.transactionHash && (<div><span className="font-semibold">Transaction Hash:</span> <span className="break-all inline-block">{t.transactionHash}</span></div>)}
            {t.fromUserId && (<div><span className="font-semibold">From User:</span> {typeof t.fromUserId === 'object' ? (t.fromUserId.email || t.fromUserId.name || t.fromUserId._id) : t.fromUserId}</div>)}
            {t.toUserId && (<div><span className="font-semibold">To User:</span> {typeof t.toUserId === 'object' ? (t.toUserId.email || t.toUserId.name || t.toUserId._id) : t.toUserId}</div>)}
            {t.tokenAmount !== undefined && (<div><span className="font-semibold">Bond Amount:</span> {formatValue(t.tokenAmount)}</div>)}
            {t.bondId && typeof t.bondId === 'object' && (<div><span className="font-semibold">Bond:</span> {t.bondId.bondName || t.bondId.bondNumber || t.bondId.isin || t.bondId.stockCode || t.bondId._id}</div>)}
            {t.bondId && typeof t.bondId !== 'object' && (<div><span className="font-semibold">Bond:</span> {t.bondId}</div>)}
            {t.token && (<div><span className="font-semibold">Token:</span> {typeof t.token === 'object' ? (t.token.symbol || t.token.name || t.token._id) : t.token}</div>)}
            {t.status && (<div><span className="font-semibold">Status:</span> {t.status}</div>)}
            {t.timestamp && (<div><span className="font-semibold">Timestamp:</span> {new Date(t.timestamp).toLocaleString()}</div>)}
        </div>
    ),
    Whitelist: (t) => (
        <div className="mt-2 space-y-1 text-sm text-gray-700">
            {t.investorName && (<div><span className="font-semibold">Investor Name:</span> {t.investorName}</div>)}
            {t.investorId && (<div><span className="font-semibold">Investor ID:</span> {t.investorId}</div>)}
            {t.bondId && (<div><span className="font-semibold">Bond:</span> {typeof t.bondId === 'object' ? (t.bondId.name || t.bondId.title || t.bondId.code || t.bondId.isin || t.bondId._id) : t.bondId}</div>)}
            {t.tokenAmount !== undefined && (<div><span className="font-semibold">Bond Amount:</span> {formatValue(t.tokenAmount)}</div>)}
            {t.allocationPercentage !== undefined && (<div><span className="font-semibold">Allocation %:</span> {formatValue(t.allocationPercentage, { pct: true, suffix: '%' })}</div>)}
            {t.walletAddress && (<div><span className="font-semibold">Wallet:</span> {typeof t.walletAddress === 'object' ? (t.walletAddress.walletAddress || t.walletAddress._id) : t.walletAddress}</div>)}
            {t.whitelistStatus && (<div><span className="font-semibold">Whitelist Status:</span> {t.whitelistStatus}</div>)}
            {t.isWhitelisted !== undefined && (<div><span className="font-semibold">Is Whitelisted:</span> {t.isWhitelisted ? 'Yes' : 'No'}</div>)}
            {t.whitelistApprovedDate && (<div><span className="font-semibold">Approved Date:</span> {new Date(t.whitelistApprovedDate).toLocaleString()}</div>)}
            {t.whitelistApprovedBy && (<div><span className="font-semibold">Approved By:</span> {typeof t.whitelistApprovedBy === 'object' ? (t.whitelistApprovedBy.email || t.whitelistApprovedBy.name || t.whitelistApprovedBy._id) : t.whitelistApprovedBy}</div>)}
            {t.whitelistRejectedDate && (<div><span className="font-semibold">Rejected Date:</span> {new Date(t.whitelistRejectedDate).toLocaleString()}</div>)}
            {t.whitelistRejectedBy && (<div><span className="font-semibold">Rejected By:</span> {typeof t.whitelistRejectedBy === 'object' ? (t.whitelistRejectedBy.email || t.whitelistRejectedBy.name || t.whitelistRejectedBy._id) : t.whitelistRejectedBy}</div>)}
            {t.whitelistRejectionReason && (<div><span className="font-semibold">Rejection Reason:</span> {t.whitelistRejectionReason}</div>)}
            {t.whitelistNotes && (<div><span className="font-semibold">Notes:</span> {t.whitelistNotes}</div>)}
            {t.whitelistTransactionHash && (<div><span className="font-semibold">Txn Hash:</span> <span className="break-all inline-block">{t.whitelistTransactionHash}</span></div>)}
            {t.level1ApprovedAt && (<div><span className="font-semibold">Level 1 Approved At:</span> {new Date(t.level1ApprovedAt).toLocaleString()}</div>)}
            {t.level1ApprovedBy && (<div><span className="font-semibold">Level 1 Approved By:</span> {typeof t.level1ApprovedBy === 'object' ? (t.level1ApprovedBy.email || t.level1ApprovedBy.name || t.level1ApprovedBy._id) : t.level1ApprovedBy}</div>)}
            {t.level2ApprovedAt && (<div><span className="font-semibold">Level 2 Approved At:</span> {new Date(t.level2ApprovedAt).toLocaleString()}</div>)}
            {t.level2ApprovedBy && (<div><span className="font-semibold">Level 2 Approved By:</span> {typeof t.level2ApprovedBy === 'object' ? (t.level2ApprovedBy.email || t.level2ApprovedBy.name || t.level2ApprovedBy._id) : t.level2ApprovedBy}</div>)}
            {t.paymentReference && (<div><span className="font-semibold">Payment Ref:</span> {t.paymentReference}</div>)}
            {t.uploadDate && (<div><span className="font-semibold">Upload Date:</span> {new Date(t.uploadDate).toLocaleString()}</div>)}
            {t.finalizedDate && (<div><span className="font-semibold">Finalized Date:</span> {new Date(t.finalizedDate).toLocaleString()}</div>)}
            {t.uploadedBy && (<div><span className="font-semibold">Uploaded By:</span> {typeof t.uploadedBy === 'object' ? (t.uploadedBy.email || t.uploadedBy.name || t.uploadedBy._id) : t.uploadedBy}</div>)}
            {t.finalizedBy && (<div><span className="font-semibold">Finalized By:</span> {typeof t.finalizedBy === 'object' ? (t.finalizedBy.email || t.finalizedBy.name || t.finalizedBy._id) : t.finalizedBy}</div>)}
        </div>
    ),
    SecondaryTransfer: (t) => (
        <div className="mt-2 space-y-1 text-sm text-gray-700">
            {t.transferRequestId && (<div><span className="font-semibold">Request ID:</span> {t.transferRequestId}</div>)}
            {t.stockCode && (<div><span className="font-semibold">Stock Code:</span> {t.stockCode}</div>)}
            {t.transferType && (<div><span className="font-semibold">Transfer Type:</span> {t.transferType}</div>)}
            {t.status && (<div><span className="font-semibold">Status:</span> {t.status}</div>)}
            {t.nominalValueTransferred !== undefined && (<div><span className="font-semibold">Nominal Value:</span> {formatValue(t.nominalValueTransferred)}</div>)}
            {t.price !== undefined && (<div><span className="font-semibold">Price:</span> {formatValue(t.price)}</div>)}
            {t.totalValue !== undefined && (<div><span className="font-semibold">Total Value:</span> {formatValue(t.totalValue)}</div>)}
            {t.currency && (<div><span className="font-semibold">Currency:</span> {t.currency}</div>)}
            {t.tradeDate && (<div><span className="font-semibold">Trade Date:</span> {new Date(t.tradeDate).toLocaleDateString()}</div>)}
            {t.valueDate && (<div><span className="font-semibold">Value Date:</span> {new Date(t.valueDate).toLocaleDateString()}</div>)}
            {t.initiatedBy && (<div><span className="font-semibold">Initiated By:</span> {t.initiatedBy}</div>)}
            {t.initiatorId && (<div><span className="font-semibold">Initiator:</span> {typeof t.initiatorId === 'object' ? (t.initiatorId.email || t.initiatorId.name || t.initiatorId._id) : t.initiatorId}</div>)}
            {t.clientId && (<div><span className="font-semibold">Client:</span> {typeof t.clientId === 'object' ? (t.clientId.email || t.clientId.name || t.clientId._id) : t.clientId}</div>)}
            {t.clientName && (<div><span className="font-semibold">Client Name:</span> {t.clientName}</div>)}
            {t.counterpartyId && (<div><span className="font-semibold">Counterparty:</span> {typeof t.counterpartyId === 'object' ? (t.counterpartyId.email || t.counterpartyId.name || t.counterpartyId._id) : t.counterpartyId}</div>)}
            {t.counterpartyName && (<div><span className="font-semibold">Counterparty Name:</span> {t.counterpartyName}</div>)}
            {t.counterpartyType && (<div><span className="font-semibold">Counterparty Type:</span> {t.counterpartyType}</div>)}
            {t.isCounterpartyValidated !== undefined && (<div><span className="font-semibold">Counterparty Validated:</span> {t.isCounterpartyValidated ? 'Yes' : 'No'}</div>)}
            {t.isAuthorized !== undefined && (<div><span className="font-semibold">Authorized:</span> {t.isAuthorized ? 'Yes' : 'No'}</div>)}
            {t.settlementReference && (<div><span className="font-semibold">Settlement Ref:</span> {t.settlementReference}</div>)}
            {t.settlementCompletedAt && (<div><span className="font-semibold">Settlement Completed:</span> {new Date(t.settlementCompletedAt).toLocaleString()}</div>)}
        </div>
    ),
};

export const normalizeTargetType = (type) => {
    if (!type || typeof type !== 'string') return '';
    const snakeToPascal = {
        token_transfer: 'TokenTransfer',
        bond: 'Bond',
        investor: 'Investor',
        payment: 'Payment',
        compliance: 'Compliance',
        user: 'User',
        whitelist: 'Whitelist',
        secondary_transfer: 'SecondaryTransfer',
    };
    const lower = type.trim().toLowerCase();
    if (snakeToPascal[lower]) return snakeToPascal[lower];
    const candidates = Object.keys(schemaFormatters);
    const match = candidates.find(k => k.toLowerCase() === lower);
    return match || (type.charAt(0).toUpperCase() + type.slice(1).toLowerCase());
};

export const formatActor = (actor) => {
    try {
        let userId = '';
        let email = '';
        if (typeof actor === 'object' && actor !== null) {
            userId = actor._id || '';
            email = actor.email || '';
        } else if (typeof actor === 'string') {
            userId = actor;
            email = /@/.test(actor) ? actor : '';
        }
        if (userId && email && userId !== email) return `${userId} (${email})`;
        if (email) return email;
        if (userId) return userId;
        return 'Unknown User';
    } catch (e) { return 'Unknown User'; }
};

export const exportAllAuditLogsCsv = async ({ role, filePrefix = 'audit_logs', pageSize = 100 }) => {
    let exporting = true;
    try {
        let page = 1;
        const allLogs = [];
        let pages = 1;
        do {
            // eslint-disable-next-line no-await-in-loop
            const res = await axios.get(`/api/audit-logs?page=${page}&limit=${pageSize}${role ? `&actorRole=${role}` : ''}`);
            const items = res.data.items || [];
            const pagination = res.data.pagination || { page, pages: 1 };
            pages = pagination.pages || pages;
            allLogs.push(...items);
            page += 1;
        } while (page <= pages);

        const headers = ['Timestamp', 'User', 'Action', 'Category', 'Result', 'IP', 'TargetType', 'TargetId', 'TargetJSON', 'DetailsJSON'];
        const escapeCsv = (val) => {
            if (val === null || val === undefined) return '';
            const str = String(val);
            if (/[,"\n]/.test(str)) return '"' + str.replace(/"/g, '""') + '"';
            return str;
        };
        const rows = allLogs.map(log => {
            const timestamp = log.timestamp ? new Date(log.timestamp).toISOString() : '';
            const user = formatActor(log.actorId);
            const action = log.action || '';
            const category = log.module || '';
            const result = log.status || '';
            const ip = log.ip || log.ipAddress || '';
            const targetTypeRaw = log.targetType || log.target?.type || '';
            const rawTargetId = (log.targetId !== undefined ? log.targetId : (log.target?.id !== undefined ? log.target?.id : log.target?._id));
            let targetId = '';
            if (typeof rawTargetId === 'object' && rawTargetId !== null) {
                targetId = rawTargetId._id || rawTargetId.id || rawTargetId.code || rawTargetId.name || '';
            } else if (typeof rawTargetId !== 'undefined') {
                targetId = String(rawTargetId);
            }
            let targetJson = '';
            if (log.target && typeof log.target === 'object') {
                try { targetJson = JSON.stringify(log.target); } catch (e) { targetJson = ''; }
            }
            let detailsJson = '';
            if (log.details && typeof log.details === 'object') {
                try { detailsJson = JSON.stringify(log.details); } catch (e) { detailsJson = ''; }
            }
            return [timestamp, user, action, category, result, ip, targetTypeRaw, targetId, targetJson, detailsJson].map(escapeCsv).join(',');
        });
        const csvContent = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const dateStamp = new Date().toISOString().replace(/[:T]/g, '-').split('.')[0];
        a.href = url;
        a.download = `${filePrefix}_${role || 'all'}_${dateStamp}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        exporting = false;
        return { success: true, count: rows.length };
    } catch (err) {
        console.error('Failed exporting audit logs', err);
        return { success: false, error: err };
    }
};

export const renderTargetDetails = (log) => {
    const targetTypeRaw = log.targetType || log.target?.type || '';
    const targetType = normalizeTargetType(targetTypeRaw);
    if (!log.target || typeof log.target !== 'object') {
        // Minimal fallback when only targetId is present (e.g., SecondaryTransfer logs without populated document)
        if (targetType === 'SecondaryTransfer') {
            const rawTargetId = (log.targetId !== undefined ? log.targetId : (log.target?.id !== undefined ? log.target?.id : log.target?._id));
            let targetId = '';
            if (typeof rawTargetId === 'object' && rawTargetId !== null) {
                targetId = rawTargetId._id || rawTargetId.id || rawTargetId.code || rawTargetId.name || '';
            } else if (typeof rawTargetId !== 'undefined') {
                targetId = String(rawTargetId);
            }
            if (targetId) {
                return (
                    <div className="mt-2 text-sm text-gray-700">
                        <div><span className="font-semibold">Secondary Transfer ID:</span> {targetId}</div>
                        <div className="text-xs text-gray-500">Full transfer details not loaded.</div>
                    </div>
                );
            }
        }
        return null;
    }
    const t = log.target;
    if (targetType && schemaFormatters[targetType]) return schemaFormatters[targetType](t);
    const exclude = new Set(['_id', '__v', 'createdAt', 'updatedAt', 'password', 'secret', 'hash']);
    const entries = Object.entries(t).filter(([k]) => !exclude.has(k));
    if (!entries.length) return <div className="mt-1 text-xs text-gray-500">No details available.</div>;
    return (
        <div className="mt-2">
            <ul className="list-disc list-inside text-sm text-gray-700">
                {entries.map(([key, value]) => (
                    <li key={key}><span className="font-semibold">{key}:</span> {formatValue(value)}</li>
                ))}
            </ul>
        </div>
    );
};
